import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  filter,
  finalize,
  map,
  mergeMap,
  Observable,
  of,
  switchMap,
  takeUntil,
  tap,
  throwError,
  withLatestFrom,
} from 'rxjs';

import { nonNullish } from '../../../lib/non-nullish';
import { AppointmentCreationService } from '../../appointment-creation/service/appointment-creation.service';
import { DialogService } from '../../dialog/service/dialog.service';
import { EventDispatcher } from '../../event-dispatcher/event-dispatcher';
import { SignupVerificationSuccessEvent } from '../../event-dispatcher/events/signup-verification-success-event';
import { LanguageService } from '../../language/service/language.service';
import { NavigationService } from '../../navigation/service/navigation.service';
import { EstimateConsultationService } from '../../online-estimate/service/estimate-consultation.service';
import { ToastService } from '../../standalone-components/toast/service/toast.service';
import { PhoneVerificationResponse } from '../api/model/phone-verification-response';
import { VerificationResponse } from '../api/model/verification-response';
import { VerificationApi } from '../api/verification.api';
import { ChangePhoneNumberDialogComponent } from '../change-phone-number-dialog/change-phone-number-dialog.component';
import { PhoneVerificationComponent } from '../phone-verification/phone-verification.component';

@Injectable({ providedIn: 'root' })
export class VerificationService {
  readonly isPhoneVerificationModalOpen$ = new BehaviorSubject<boolean>(false);
  readonly isPhoneVerified$ = new BehaviorSubject<boolean>(false);
  readonly isChangePhoneNumberSubmited$ = new BehaviorSubject<boolean>(false);
  readonly isPhoneCodeSent$ = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly toastService: ToastService,
    private readonly dialogService: DialogService,
    private readonly eventDispatcher: EventDispatcher,
    private readonly languageService: LanguageService,
    private readonly verificationApi: VerificationApi,
    private readonly navigationService: NavigationService,
    private readonly appointmentCreationService: AppointmentCreationService,
    private readonly estimateConsultationService: EstimateConsultationService,
  ) {}

  getVerificationStatus(): Observable<VerificationResponse> {
    return this.verificationApi.verifyUser();
  }

  removeLocalStorageNeedsEmailVerification() {
    localStorage.removeItem('needsEmailVerification');
  }

  removeLocalStorageNeedsPhoneVerification() {
    localStorage.removeItem('needsPhoneVerification');
  }

  getRouteToNavigateTo(data: VerificationResponse) {
    if (data.verified) {
      return null;
    }
    return !data.verifiedByPhone
      ? '/verification/phone'
      : '/verification/email';
  }

  verifyPhoneNumber(
    code: string,
  ): Observable<
    [PhoneVerificationResponse, boolean, VerificationResponse, number]
  > {
    this.isPhoneCodeSent$.next(true);

    return this.verificationApi
      .verifyPhoneNumber(code)
      .pipe(switchMap((res) => this.processVerifyPhoneNumberResponse(res)));
  }

  verifyPhoneNumberPublic(
    code: string,
    userId: string,
  ): Observable<PhoneVerificationResponse> {
    return this.verificationApi.verifyPhoneNumberPublic(code, userId).pipe(
      tap((response) => {
        if (response.verified) {
          this.removeLocalStorageNeedsPhoneVerification();
          this.eventDispatcher.dispatch(
            new SignupVerificationSuccessEvent({
              email: response.emailAddress,
              phone: response.phoneNumber,
              userId: response.userId.toString(),
              patientUuid: response.patientUuid,
            }),
          );
        } else {
          this.toastService.show($localize`Incorrect verification code`);
        }
      }),
    );
  }

  verifyPhoneNumberSairen(
    code: string,
    userId: string,
    sessionId: string,
  ): Observable<PhoneVerificationResponse> {
    return this.verificationApi
      .verifyPhoneNumberSairen(code, userId, sessionId)
      .pipe(
        tap((response) => {
          if (response.verified) {
            this.removeLocalStorageNeedsPhoneVerification();
            this.eventDispatcher.dispatch(
              new SignupVerificationSuccessEvent({
                email: response.emailAddress,
                phone: response.phoneNumber,
                userId: response.userId.toString(),
                patientUuid: response.patientUuid,
              }),
            );
          } else {
            this.toastService.show($localize`Incorrect verification code`);
          }
        }),
      );
  }

  sendVerificationCodeToPhone(): Observable<PhoneVerificationResponse> {
    const language = this.languageService.getLanguage();
    return this.verificationApi.sendVerificationCodeToPhone(
      nonNullish(language),
    );
  }

  sendVerificationEmail(): Observable<PhoneVerificationResponse> {
    const language = this.languageService.getLanguage();
    return this.verificationApi.sendVerificationEmail(nonNullish(language));
  }

  changePhoneNumber(phoneNumber: string) {
    this.isChangePhoneNumberSubmited$.next(true);
    return this.verificationApi.changePhoneNumber(phoneNumber).pipe(
      catchError((err: unknown) => {
        this.isChangePhoneNumberSubmited$.next(false);
        return throwError(() => err);
      }),
    );
  }

  displayChangePhoneNumberDialog() {
    const dialogRef = this.dialogService.open(
      ChangePhoneNumberDialogComponent,
      { isConfirmButtonClicked$: this.isChangePhoneNumberSubmited$ },
    );
    dialogRef.dialog.submitted
      .pipe(
        mergeMap((phoneNumber) => this.changePhoneNumber(phoneNumber)),
        mergeMap(() => this.sendVerificationCodeToPhone()),
        tap(() =>
          this.toastService.show($localize`Successfully updated phone number!`),
        ),
        finalize(() => this.isChangePhoneNumberSubmited$.next(false)),
        tap(dialogRef.close),
        takeUntil(dialogRef.closed),
      )
      .subscribe();
  }

  displayPhoneCodeVerificationModal() {
    const dialogRef = this.dialogService.open(
      PhoneVerificationComponent,
      undefined,
      'lg',
    );
    this.isPhoneVerificationModalOpen$.next(true);
    dialogRef.dialog.submitted
      .pipe(
        mergeMap(() =>
          this.getVerificationStatus().pipe(
            tap((res) => this.isPhoneVerified$.next(res.verifiedByPhone)),
            tap(() => this.isPhoneVerificationModalOpen$.next(false)),
            tap((res) => {
              if (res.verifiedByPhone) {
                dialogRef.close();
              }
            }),
          ),
        ),
        takeUntil(dialogRef.closed),
      )
      .subscribe();
  }

  verifyEmail(): Observable<PhoneVerificationResponse> {
    const params = this.navigationService.getQueryParams();

    return this.verificationApi
      .verifyEmail({
        email: params['email'],
        verificationCode: params['code'],
      })
      .pipe(
        tap((res) => {
          if (res.verified) {
            this.removeLocalStorageNeedsEmailVerification();
            this.navigationService.navigateToWithoutAdditionParams('/account');
          }
        }),
      );
  }

  processVerifyPhoneNumberResponse(
    response: PhoneVerificationResponse,
  ): Observable<
    [PhoneVerificationResponse, boolean, VerificationResponse, number]
  > {
    return of(response).pipe(
      filter((res) => !!res),
      withLatestFrom(
        this.estimateConsultationService
          .isOnlineEstimatePhoneVerificationProcessAsObservable$,
        this.appointmentCreationService.currentStep$,
      ),
      switchMap(([res, isOnlineEstimatePhoneVerification, step]) =>
        this.getVerificationStatus().pipe(
          map(
            (verificationStatus) =>
              [
                res,
                isOnlineEstimatePhoneVerification,
                verificationStatus,
                step,
              ] as [
                PhoneVerificationResponse,
                boolean,
                VerificationResponse,
                number,
              ],
          ),
        ),
      ),
      tap(
        ([
          res,
          isOnlineEstimatePhoneVerification,
          verificationStatus,
          currentStep,
        ]) => {
          this.isPhoneVerified$.next(res.verified);
          if (res.verified) {
            this.removeLocalStorageNeedsPhoneVerification();
            this.removeLocalStorageNeedsEmailVerification();
          }
          if (
            !verificationStatus.verified &&
            verificationStatus.verifiedByPhone
          ) {
            localStorage.setItem('needsEmailVerification', 'true');
            this.navigationService.navigateToWithoutMedicalCenter(
              '/verification/email',
            );
          } else if (res.verified && isOnlineEstimatePhoneVerification) {
            this.navigationService.navigateToWithoutMedicalCenter(
              '/estimates/verify-user',
            );
          } else if (res.verified && res.patientUuid) {
            if (currentStep !== 1) {
              this.navigationService.navigateToWithoutMedicalCenter(
                '/confirmation',
              );
            } else {
              this.navigationService.navigateToWithoutMedicalCenter('/account');
            }
          } else if (res.verified) {
            if (currentStep !== 1) {
              this.navigationService.navigateWithRedirectionPathAndWithoutMedicalCenter(
                'patient',
                '/confirmation',
              );
            } else {
              this.navigationService.navigateWithRedirectionPathAndWithoutMedicalCenter(
                'patient',
                'account',
              );
            }
            this.eventDispatcher.dispatch(
              new SignupVerificationSuccessEvent({
                email: res.emailAddress,
                phone: res.phoneNumber,
                userId: res.userId.toString(),
                patientUuid: res.patientUuid,
              }),
            );
          } else {
            this.toastService.show($localize`Incorrect verification code`);
          }
        },
      ),
      finalize(() => this.isPhoneCodeSent$.next(false)),
    );
  }
}
